import {
  DownloadOutlined,
  FileDoneOutlined,
  UploadOutlined
} from '@ant-design/icons'
import { Button, Col, message, Row, Tag } from 'antd'
import { ColumnsType, TablePaginationConfig } from 'antd/lib/table'
import axios from 'axios'
import { handleServerTableChange } from 'components/ServerTable/function'
import ServerTable from 'components/ServerTable/ServerTable'

import PricingModel from 'egi/models/PricingModel'
import {
  downloadModelRepository,
  pricingRepository
} from 'egi/repositories/ImportRepository'

import { useAuth, useTables } from 'hooks'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { RootState } from 'store/modules/rootReducer'
import { tablesSetFiltersValues } from 'store/modules/tables/actions'
import { reactAppServer } from 'utils/config'

import { downloadFile } from 'utils/download'
import { isLocal } from 'utils/globals'
import { ImportActions } from 'store/modules/import/importActions'
import {
  IFees,
  IImportFile,
  Import,
  ILineErrors,
  IImportTypes
} from '../importInterfaces'
import CurrentTableModal from '../components/CurrentTableModal/CurrentTableModal'
import DisplayErrorsModal from '../components/DisplayErrorsModal/DisplayErrorsModal'
import ImportHintModal from '../components/ImportHintModal/ImportHintModal'
import LoadingImportBar from '../components/LoadingImportBar/LoadingImportBar'
import UploadImportModal from '../components/UploadImportModal/UploadImportModal'
import UploadImportModelModal from '../components/UploadImportModelModal/UploadImportModelModal'

function ImportPage ({ type }: { type: IImportTypes }) {
  const user = useAuth()
  const dispatch = useDispatch()
  const table = useTables()
  const isNotaryFees = type === IImportTypes.notaryFees

  const humanizedType = isNotaryFees
    ? 'Custas Cartorárias'
    : 'Módulo de Saldo Devedor'

  const [loadingDownload, setLoadingDownload] = useState<boolean>()
  const [visibleUploadModel, setVisibleUploadModel] = useState<boolean>(false)
  const [visibleUpload, setVisibleUpload] = useState<boolean>(false)
  const [visibleHint, setVisibleHint] = useState<boolean>(false)

  const [visibleLast, setVisibleLast] = useState<boolean>(false)

  const [fees, setFees] = useState<IFees>()
  const [lastFeesError, setLastFeesError] = useState<string>()
  const [lastFeesLoading, setLastFeesLoading] = useState<boolean>(false)

  const loadingState = useSelector((state: RootState) => state.loadingReducer)

  const setFileImport = useCallback(
    (payload: IImportFile | undefined) => {
      dispatch(ImportActions.setImportItem(payload))
    },
    [dispatch]
  )

  const onCancel = useCallback(() => {
    setVisibleUpload(false)
    setVisibleUploadModel(false)
    setVisibleHint(false)
    setVisibleLast(false)
  }, [])

  const onImport = useCallback(
    (data: IImportFile | undefined) => setFileImport(data),
    [setFileImport]
  )

  const onDownloadModelLink = useCallback((id: string) => {
    const base = isLocal ? `${reactAppServer}/download/models/` : `${reactAppServer}download/models/`
    return `${base}${id}`
  }, [])

  const onDownloadOutstandingBalance = useCallback(async (row: Import) => {
    try {
      const blob = await pricingRepository.downloadOutstandingBalanceFile(row._id)
      const objectUrl = window.URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = objectUrl
      link.download = row.fileName
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      window.URL.revokeObjectURL(objectUrl)
    } catch (err: any) {
      message.error(err.message)
    }
  }, [])

  const columns: ColumnsType<Import> = useMemo(
    () => [
      {
        align: 'center',
        title: 'ID',
        dataIndex: isNotaryFees ? 'notaryFeeCode' : 'outstandingBalanceCode',
        width: '5rem'
      },
      {
        align: 'center',
        title: 'Vigente',
        dataIndex: 'isCurrentTable',
        render: (isCurrentTable: boolean) => {
          if (isCurrentTable === true) {
            return (
              <Tag
                color='var(--green-status)'
                onClick={() => isNotaryFees && setVisibleLast(true)}
              >
                Vigente
              </Tag>
            )
          }
          return '-'
        }
      },
      {
        title: 'Data de Importação',
        dataIndex: 'createdAt'
      },
      {
        title: 'Nome de usuário',
        dataIndex: ['user', 'name']
      },
      {
        title: 'Nome Arquivo',
        dataIndex: 'fileName'
      },
      {
        align: 'center',
        title: 'Erro',
        dataIndex: 'lineErrors',
        render: (value: ILineErrors[], row: Import) => {
          if (!value.length && !row?.fileError?.length) return 'Não'
          return <DisplayErrorsModal type={type} row={row} />
        }
      },
      {
        title: 'Download',
        dataIndex: 'url',
        width: '5rem',
        align: 'center',
        render: (url: string, row: Import) => {
          if (!isNotaryFees) {
            return (
              <button
                type='button'
                className='import__download-button'
                onClick={() => onDownloadOutstandingBalance(row)}
              >
                <DownloadOutlined className='import__download-icon' />
              </button>
            )
          }

          return (
            <a href={url}>
              {' '}
              <DownloadOutlined className='import__download-icon' />
            </a>
          )
        }
      }
    ],
    [isNotaryFees, onDownloadOutstandingBalance, type]
  )

  const showModel = useCallback(async () => {
    setLoadingDownload(true)
    try {
      const response = isNotaryFees
        ? await downloadModelRepository.showNotaryFeesModel()
        : await downloadModelRepository.showOutstandingBalanceModel()

      const fileId = response?.data?.data?.downloadModel?._id
      if (!fileId) throw new Error('Modelo não encontrado')

      const fileUrl = onDownloadModelLink(fileId)

      const typeMap = {
        [IImportTypes.notaryFees]: 'custas_cartorarias',
        [IImportTypes.outstandingBalance]: 'modulo_saldo_devedor'
      }
      const titleType = typeMap[type]
      const currentYear = new Date().getFullYear()
      const title = `${titleType}_V${currentYear}`

      await axios.head(fileUrl)
      downloadFile({ fileUrl, title })
    } catch (err: any) {
      message.error(err.message)
    } finally {
      setLoadingDownload(false)
    }
  }, [isNotaryFees, onDownloadModelLink, type])

  const getLast = useCallback(async () => {
    if (!isNotaryFees) return
    setLastFeesLoading(true)
    setLastFeesError(undefined)
    try {
      const { data } = await pricingRepository.getLastNotaryFees()
      const feesData = data?.data?.notaryFee?.fees
      if (!feesData) throw new Error('Não há nenhuma tabela vigente no momento')
      setFees(feesData)
    } catch (err: any) {
      setLastFeesError(err.message)
    } finally {
      setLastFeesLoading(false)
    }
  }, [isNotaryFees])

  useEffect(() => {
    getLast()
  }, [getLast])

  return (
    <div className='import'>
      <UploadImportModal
        type={type}
        visible={visibleUpload}
        onCancel={onCancel}
        onImportData={onImport}
      />

      <ImportHintModal type={type} visible={visibleHint} onCancel={onCancel} />

      {isNotaryFees && (
        <CurrentTableModal
          visible={visibleLast}
          onCancel={onCancel}
          fees={fees}
          loading={lastFeesLoading}
          error={lastFeesError}
        />
      )}

      {PricingModel.canEditImportModel(user.level) && (
        <UploadImportModelModal
          type={type}
          visible={visibleUploadModel}
          onCancel={onCancel}
        />
      )}

      <Row gutter={10} className='mb-3'>
        <Col lg={12} sm={24}>
          <div className='import__card'>
            <div className='flex'>
              <div>
                <div className='import__title'>
                  <h2 className='import__card-title'>
                    Importação de {humanizedType}
                  </h2>
                  <p>
                    Importe sua planilha de{' '}
                    {humanizedType.toLowerCase()}
                  </p>
                </div>
                <div className='import__buttons'>
                  <Row className='import__buttons-top'>
                    <Button
                      type='primary'
                      icon={<UploadOutlined />}
                      className='m-1'
                      onClick={() => setVisibleUpload(true)}
                    >
                      Importar planilha
                    </Button>
                    <Button
                      type='primary'
                      icon={<DownloadOutlined />}
                      className='m-1'
                      onClick={showModel}
                      loading={loadingDownload}
                    >
                      Baixar modelo
                    </Button>
                  </Row>
                  <Row className='import__buttons-bottom'>
                    {isNotaryFees && (
                      <Button
                        type='primary'
                        className='m-1'
                        onClick={() => setVisibleLast(true)}
                      >
                        Ver tabela de vigente
                      </Button>
                    )}

                    {PricingModel.canEditImportModel(user.level) && (
                      <Button
                        className='m-1'
                        icon={<UploadOutlined />}
                        onClick={() => setVisibleUploadModel(true)}
                        type='primary'
                      >
                        Anexar novo modelo
                      </Button>
                    )}
                  </Row>
                </div>
              </div>
              <div className='import__icon'>
                <FileDoneOutlined className='white import__icon-bg' />
              </div>
            </div>
          </div>
        </Col>
        <Col lg={12} sm={24}>
          <div className='import__card import__card-mobile'>
            <div className='text-center'>
              <h2 className='import__card-title'>
                Siga as instruções para realizar a importação
              </h2>
              <p className=' mb-2'>
                Baixe o modelo e siga as instruções, clicando no botão abaixo,
                para preencher corretamente a tabela de{' '}
                {humanizedType.toLowerCase()}
              </p>
              <Button type='primary' onClick={() => setVisibleHint(true)}>
                Instruções
              </Button>
            </div>
          </div>
        </Col>
      </Row>

      <LoadingImportBar
        type={type}
        importFile={loadingState?.importItem}
        onImport={onImport}
      />

      <h2 className='import__card-title mt-3'>Lista de importações</h2>
      <ServerTable
        onChange={(
          pagination: TablePaginationConfig,
          filters: Record<string, (string | number | boolean)[] | null>,
          sorter
        ) => {
          const serverTableFiltersData = handleServerTableChange(
            columns,
            table.filters
          )(pagination, filters, sorter)
          dispatch(
            tablesSetFiltersValues({
              ...table.filters,
              ...serverTableFiltersData
            })
          )
        }}
        url={`/pricings/${
          isNotaryFees ? 'notary-fees' : 'outstanding-balance'
        }/list`}
        columns={columns}
      />
    </div>
  )
}

export default ImportPage
